import { AsyncPipe, NgIf } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  Input,
  ViewEncapsulation,
} from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
import { TimezoneService } from '@sc/data-access-configuration';
import {
  Dossier,
  allImpactsTypes,
  getGlassNameKey,
  getOperationWithGlassKey,
} from '@sc/data-access-models';
import { DossierSandboxService } from '@sc/data-access-state-management';
import { LocaleDatePipe, TranslatorService } from '@sc/data-access-translation';
import { MomentParseZonePipe } from '../../pipes';
import { CarImgSummaryComponent } from '../car-img-summary';
import { GreenBoxComponent } from '../green-box';
import { IconComponent } from '../icon';

@Component({
  selector: 'sc-sinister-type',
  styleUrls: ['./sinister-type.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    NgIf,
    IconComponent,
    CarImgSummaryComponent,
    GreenBoxComponent,
    MomentParseZonePipe,
    TranslateModule,
    LocaleDatePipe,
    AsyncPipe,
  ],
  template: `
    <h2 *ngIf="mainTitle" class="sc-design-title-h1">{{ mainTitle }}</h2>
    <div class="sinister-type-container">
      <section class="left">
        <span class="sinister-informations">
          <div class="info" *ngIf="dossier.claim?.glass">
            <sc-icon name="fitter"></sc-icon>
            <span>
              {{
                isOutsideIntervention()
                  ? ('GlobalApplicativeConstants.interventionType.outsideIntervention'
                    | translate)
                  : ('GlobalApplicativeConstants.interventionType.inCenter'
                    | translate)
              }}
            </span>
            <span>{{ getOperation() }}</span>
          </div>

          <div class="info">
            <sc-icon name="car"></sc-icon>
            <span>{{ dossier.claim?.car?.licensePlateNumber }}</span>
            <span *ngIf="dossier.claim?.car?.manufacturer">{{
              dossier.claim?.car?.manufacturer
            }}</span>
            <span *ngIf="dossier.claim?.car?.model">{{
              dossier.claim?.car?.model
            }}</span>
          </div>

          <div class="info" *ngIf="dossier.claim?.glass?.impacts?.impactsType">
            <sc-icon name="impact"></sc-icon>
            <span>
              {{
                impactsTypes[dossier.claim?.glass?.impacts?.impactsType]
                  | translate
              }}
            </span>
          </div>

          <div
            class="info"
            *ngIf="isBoolValue(dossier.claim?.glass?.impacts?.inVisionField)"
          >
            <sc-icon name="vision_field"></sc-icon>
            <span>
              {{
                dossier.claim?.glass?.impacts?.inVisionField
                  ? ('GlobalApplicativeConstants.sinisterGlass.glassImpact.inVisionField'
                    | translate)
                  : ('GlobalApplicativeConstants.sinisterGlass.glassImpact.outVisionField'
                    | translate)
              }}
            </span>
          </div>

          <div class="info" *ngIf="dossier.claim?.glass?.impacts?.impactsType">
            <sc-icon name="part_diameter"></sc-icon>
            <span>
              {{
                dossier.claim?.glass?.impacts?.size === 'smaller2cm'
                  ? ('GlobalApplicativeConstants.sinisterGlass.glassImpact.smaller2cm'
                    | translate)
                  : ('GlobalApplicativeConstants.sinisterGlass.glassImpact.bigger2cm'
                    | translate)
              }}
            </span>
          </div>

          <div
            class="info"
            *ngIf="isBoolValue(dossier.claim?.glass?.impacts?.atOuterEdge)"
          >
            <sc-icon name="four_cm"></sc-icon>
            <span>
              {{
                dossier.claim?.glass?.impacts?.atOuterEdge
                  ? ('GlobalApplicativeConstants.sinisterGlass.glassImpact.atOuterEdge'
                    | translate)
                  : ('GlobalApplicativeConstants.sinisterGlass.glassImpact.atInnerEdge'
                    | translate)
              }}
            </span>
          </div>
        </span>
      </section>

      <section class="right">
        <sc-car-img-summary
          [glass]="dossier?.claim?.glass"
          [editMode]="false"
        ></sc-car-img-summary>

        <sc-green-box *ngIf="showGreenBox">
          <div class="intervention">
            <span *ngIf="!!dossier.appointment" class="title"
              >{{ 'SinisterType.interventionTitle' | translate }}
              {{
                'GlobalApplicativeConstants.time.dateFormat'
                  | translate
                    : {
                        dateDay:
                          dossier?.appointment?.time
                          | parseZoneTimeString
                            : timezoneService.getTimezone(dossier?.appointment)
                          | localeDate: 'fullDate'
                          | async,
                        isHalfDay: dossierSB.shouldDisplayAppointmentHalfDay(
                          dossier?.appointment
                        ),
                        dateTime:
                          dossier?.appointment?.time
                          | parseZoneTimeString
                            : timezoneService.getTimezone(dossier?.appointment)
                          | localeDate: 'shortTime'
                          | async,
                        appointmentHalfDay: dossierSB.getTime(
                          dossier?.appointment
                        )
                      }
              }}
            </span>
            <span class="about">{{ getOperation() }} </span
            ><span>{{
              isReplacementVehicleSelected()
                ? ('SinisterType.withVehicle' | translate)
                : ('SinisterType.withoutVehicle' | translate)
            }}</span>
            <span class="address">{{ getAddress() }}</span>
          </div>
        </sc-green-box>
      </section>
    </div>
  `,
})
export class SinisterTypeComponent {
  @Input() dossier: Dossier;
  @Input() pad: boolean;
  @Input() reparation: boolean;
  @Input() showGreenBox = true;
  @Input() mainTitle: string;

  impactsTypes = Object.fromEntries(
    allImpactsTypes.map(({ key, value }) => [key, value])
  );

  constructor(
    private readonly translator: TranslatorService,
    public dossierSB: DossierSandboxService,
    public timezoneService: TimezoneService
  ) {}

  isBoolValue(obj: any) {
    return obj === true || obj === false;
  }

  getAddress() {
    return (
      this.dossier?.appointment?.interventionLocation?.address ||
      this.dossier?.requestAppointment?.interventionLocation?.address ||
      '-'
    );
  }

  getOperation(): string {
    return this.translator.getInstantTranslation(
      getOperationWithGlassKey(this.dossier?.claim?.operationType),
      {
        glassName: this.translator
          .getInstantTranslation(getGlassNameKey(this.dossier.claim.glass))
          .toLowerCase(),
      }
    );
  }

  isOutsideIntervention() {
    return (
      this.dossier?.appointment?.outsideIntervention ??
      this.dossier?.requestAppointment?.outsideIntervention
    );
  }

  isReplacementVehicleSelected() {
    return (
      this.dossier?.appointment?.isReplacementVehicleSelected ??
      this.dossier?.requestAppointment?.isReplacementVehicleSelected
    );
  }
}
